import { useCallback, useRef, useState } from "react";
import { ConfirmContent, ItemWithMetaData } from "../api/Items";
import {
  useCreateItemPostRating,
  useRemoveRatingAndKeywordsPostsItem,
} from "../api/ItemsApi";
import {
  useManualUpdateProfileContent,
  useRemoveProfileAndPosts,
} from "../api/ProfilesApi";
import {
  buildProfileConfirmPayload,
  ProfileAddConfirmation,
} from "./profileConfirmEntities";

type ProfileOperationType = "add" | "remove";

type QueuedOperation = {
  itemUUID: string;
  type: ProfileOperationType;
  confirmation?: ProfileAddConfirmation;
  existingContent?: ConfirmContent;
};

export type ProfilerOptimisticOverride = "add" | "remove";

/** Max concurrent add/remove API calls so rapid clicks aren't serialized. */
export const MAX_CONCURRENT_PROFILE_OPERATIONS = 6;

export function applyProfilerOptimisticOverrides(
  items: ItemWithMetaData[],
  overrides: ReadonlyMap<string, ProfilerOptimisticOverride>
): ItemWithMetaData[] {
  if (overrides.size === 0) {
    return items;
  }

  return items.flatMap((profile) => {
    const override = overrides.get(profile.item.external_id);
    if (override === "remove") {
      return [];
    }
    if (override === "add") {
      return [
        {
          ...profile,
          item: {
            ...profile.item,
            in_report: true,
            is_reviewed: true,
          },
        },
      ];
    }
    return [profile];
  });
}

export function useProfilerProfileOperations({
  loadProfilesConfirm,
  onError,
  onSuccess,
  onProfileAdded,
}: {
  loadProfilesConfirm?: () => void | Promise<void>;
  onError?: (message: string) => void;
  onSuccess?: () => void;
  onProfileAdded?: () => void;
}) {
  const queueRef = useRef<QueuedOperation[]>([]);
  const processingRef = useRef(false);
  const processScheduledRef = useRef(false);
  const wakeRef = useRef<(() => void) | null>(null);
  const pendingRef = useRef<Set<string>>(new Set());

  const [pendingItemUUIDs, setPendingItemUUIDs] = useState<Set<string>>(
    () => new Set()
  );
  const [optimisticOverrides, setOptimisticOverrides] = useState<
    Map<string, ProfilerOptimisticOverride>
  >(() => new Map());
  const [recentlyAddedOrder, setRecentlyAddedOrder] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [queuedCount, setQueuedCount] = useState(0);
  const [isAwaitingReload, setIsAwaitingReload] = useState(false);

  const { mutateAsync: createItemRating } = useCreateItemPostRating();
  const { mutateAsync: removeRating } = useRemoveRatingAndKeywordsPostsItem();
  const { mutateAsync: updateProfileContent } = useManualUpdateProfileContent();
  const { mutateAsync: removeProfile } = useRemoveProfileAndPosts();

  const addPending = useCallback((itemUUID: string) => {
    pendingRef.current.add(itemUUID);
    setPendingItemUUIDs(new Set(pendingRef.current));
  }, []);

  const removePending = useCallback((itemUUID: string) => {
    pendingRef.current.delete(itemUUID);
    setPendingItemUUIDs(new Set(pendingRef.current));
  }, []);

  const clearOptimisticOverride = useCallback((itemUUID: string) => {
    setOptimisticOverrides((prev) => {
      if (!prev.has(itemUUID)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(itemUUID);
      return next;
    });
  }, []);

  const wakeProcessor = useCallback(() => {
    const wake = wakeRef.current;
    if (!wake) {
      return;
    }
    wakeRef.current = null;
    wake();
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current) {
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);
    let needsReload = false;

    const runOperation = async (operation: QueuedOperation) => {
      let ratingCreated = false;

      try {
        if (operation.type === "add") {
          await createItemRating({
            item_uuid: operation.itemUUID,
            ratings: [{ name: "CONSTELLATION", score_type: "recommendation" }],
          });
          ratingCreated = true;

          const profileConfirmContent = buildProfileConfirmPayload(
            operation.confirmation,
            operation.existingContent
          );
          if (profileConfirmContent) {
            await updateProfileContent({
              item_uuid: operation.itemUUID,
              content: profileConfirmContent,
            });
          }

          onProfileAdded?.();
          onSuccess?.();
          needsReload = true;
        } else {
          await removeProfile(operation.itemUUID);
          removePending(operation.itemUUID);
          onSuccess?.();
          needsReload = true;
        }
      } catch {
        if (ratingCreated) {
          try {
            await removeRating({ item_uuids: [operation.itemUUID] });
          } catch {
            // Best-effort rollback; still surface add error to the user.
          }
        }
        removePending(operation.itemUUID);
        clearOptimisticOverride(operation.itemUUID);
        onError?.(
          operation.type === "add"
            ? "Error adding profile, please contact support"
            : "Failed to remove profile. Please try again."
        );
      }
    };

    try {
      const inFlight = new Set<Promise<void>>();
      const inFlightUUIDs = new Set<string>();

      const takeNextRunnableOperation = (): QueuedOperation | undefined => {
        const nextIndex = queueRef.current.findIndex(
          (operation) => !inFlightUUIDs.has(operation.itemUUID)
        );
        if (nextIndex < 0) {
          return undefined;
        }
        const [operation] = queueRef.current.splice(nextIndex, 1);
        setQueuedCount(queueRef.current.length);
        return operation;
      };

      const waitForProgress = () =>
        new Promise<void>((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) {
              return;
            }
            settled = true;
            if (wakeRef.current === finish) {
              wakeRef.current = null;
            }
            resolve();
          };
          wakeRef.current = finish;
          void Promise.race(inFlight).then(finish);
        });

      while (queueRef.current.length > 0 || inFlight.size > 0) {
        while (inFlight.size < MAX_CONCURRENT_PROFILE_OPERATIONS) {
          const operation = takeNextRunnableOperation();
          if (!operation) {
            break;
          }

          inFlightUUIDs.add(operation.itemUUID);
          const tracked = runOperation(operation).finally(() => {
            inFlight.delete(tracked);
            inFlightUUIDs.delete(operation.itemUUID);
          });
          inFlight.add(tracked);
        }

        if (inFlight.size === 0) {
          break;
        }

        await waitForProgress();
      }

      if (needsReload) {
        const pendingBeforeReload = new Set(pendingRef.current);
        setIsAwaitingReload(true);
        try {
          await loadProfilesConfirm?.();
          pendingBeforeReload.forEach((uuid) => {
            pendingRef.current.delete(uuid);
          });
          setPendingItemUUIDs(new Set(pendingRef.current));
          setOptimisticOverrides((prev) => {
            const next = new Map<string, ProfilerOptimisticOverride>();
            prev.forEach((value, key) => {
              if (value === "remove") {
                next.set(key, value);
              } else if (value === "add" && pendingRef.current.has(key)) {
                next.set(key, value);
              }
            });
            return next;
          });
        } catch {
          onError?.("Error refreshing profiles, please try again");
        } finally {
          setIsAwaitingReload(false);
        }
      }
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
      setQueuedCount(queueRef.current.length);
      wakeRef.current = null;

      if (queueRef.current.length > 0) {
        void processQueue();
      }
    }
  }, [
    clearOptimisticOverride,
    createItemRating,
    loadProfilesConfirm,
    onError,
    onSuccess,
    onProfileAdded,
    removePending,
    removeProfile,
    removeRating,
    updateProfileContent,
  ]);

  const scheduleProcessQueue = useCallback(() => {
    if (processingRef.current) {
      // New work arrived while workers are waiting; reopen the pump.
      wakeProcessor();
      return;
    }
    if (processScheduledRef.current) {
      return;
    }
    processScheduledRef.current = true;
    // Defer so rapid same-tick enqueues batch into one parallel wave.
    queueMicrotask(() => {
      processScheduledRef.current = false;
      void processQueue();
    });
  }, [processQueue, wakeProcessor]);

  const enqueue = useCallback(
    (
      itemUUID: string,
      type: ProfileOperationType,
      confirmation?: ProfileAddConfirmation,
      existingContent?: ConfirmContent
    ): boolean => {
      if (!itemUUID) {
        return false;
      }

      const alreadyPending = pendingRef.current.has(itemUUID);

      if (alreadyPending) {
        if (type === "remove") {
          queueRef.current = queueRef.current.filter(
            (operation) =>
              !(operation.itemUUID === itemUUID && operation.type === "add")
          );
          const hasRemoveQueued = queueRef.current.some(
            (operation) =>
              operation.itemUUID === itemUUID && operation.type === "remove"
          );
          if (!hasRemoveQueued) {
            queueRef.current.push({ itemUUID, type: "remove" });
            setQueuedCount(queueRef.current.length);
          }
          setOptimisticOverrides((overrides) =>
            new Map(overrides).set(itemUUID, "remove")
          );
          scheduleProcessQueue();
          return true;
        }

        return false;
      }

      addPending(itemUUID);
      if (type === "add") {
        setOptimisticOverrides((overrides) =>
          new Map(overrides).set(itemUUID, "add")
        );
        setRecentlyAddedOrder((prev) => [
          itemUUID,
          ...prev.filter((id) => id !== itemUUID),
        ]);
      } else {
        setOptimisticOverrides((overrides) =>
          new Map(overrides).set(itemUUID, "remove")
        );
      }
      queueRef.current.push({ itemUUID, type, confirmation, existingContent });
      setQueuedCount(queueRef.current.length);
      scheduleProcessQueue();
      return true;
    },
    [addPending, scheduleProcessQueue]
  );

  const applyOptimisticProfiles = useCallback(
    (items: ItemWithMetaData[]) =>
      applyProfilerOptimisticOverrides(items, optimisticOverrides),
    [optimisticOverrides]
  );

  const hasPendingOperations =
    pendingItemUUIDs.size > 0 ||
    queuedCount > 0 ||
    isProcessing ||
    isAwaitingReload;

  return {
    enqueueAdd: (
      itemUUID: string,
      confirmation?: ProfileAddConfirmation,
      existingContent?: ConfirmContent
    ) => enqueue(itemUUID, "add", confirmation, existingContent),
    enqueueRemove: (itemUUID: string) => enqueue(itemUUID, "remove"),
    pendingItemUUIDs,
    recentlyAddedOrder,
    hasPendingOperations,
    applyOptimisticProfiles,
  };
}
